import { DunningStage } from './types.js';
/**
 * The DunningStage escalation ladder — the single source of truth for the order
 * in which dunning levels are issued for an invoice.
 *
 * A dunning letter is always exactly one step further than the highest level
 * already issued: a fresh invoice escalates to `PAYMENT_REMINDER`, then
 * `FIRST_NOTICE`, then `FINAL_NOTICE`, after which no further dunning is
 * possible. Candidate selection, run execution, the per-item processor, the
 * status query, and the `DunningLetter` aggregate all derive "what comes next"
 * from this one array — extend it here and every site escalates correctly.
 */
export const DUNNING_STAGE_SEQUENCE = [
    DunningStage.PAYMENT_REMINDER,
    DunningStage.FIRST_NOTICE,
    DunningStage.FINAL_NOTICE,
];
/** The maximum number of dunning letters issuable per invoice. */
export const MAX_DUNNING_STAGE_COUNT = DUNNING_STAGE_SEQUENCE.length;
/**
 * Position of a DunningStage in the ladder, 0-based. Returns `-1` for a value not
 * in the sequence (shouldn't happen for a valid `DunningStage`).
 */
export function dunningStageRank(stage) {
    return DUNNING_STAGE_SEQUENCE.indexOf(stage);
}
/**
 * The next DunningStage after `current`, or `null` if `current` is already the
 * final level. Passing `null` returns the first level (`PAYMENT_REMINDER`) —
 * the entry point for an invoice that has never been dunned.
 */
export function nextDunningStage(current) {
    if (current === null)
        return DUNNING_STAGE_SEQUENCE[0];
    return DUNNING_STAGE_SEQUENCE[dunningStageRank(current) + 1] ?? null;
}
/**
 * The highest DunningStage among those already `issued`, or `null` if none have
 * been issued.
 */
export function highestDunningStage(issued) {
    let highest = null;
    let highestRank = -1;
    for (const stage of issued) {
        const rank = dunningStageRank(stage);
        if (rank > highestRank) {
            highestRank = rank;
            highest = stage;
        }
    }
    return highest;
}
/**
 * The next DunningStage to issue given the set of levels already `issued`, or
 * `null` if the final level has been reached. Equivalent to
 * `nextDunningStage(highestDunningStage(issued))`.
 */
export function nextDunningStageAfter(issued) {
    return nextDunningStage(highestDunningStage(issued));
}
/**
 * Whether `candidate` is exactly the next level to issue given the set of
 * levels already `issued` — i.e. it neither skips, repeats, nor exceeds the
 * ladder.
 */
export function isStrictlyNextDunningStage(issued, candidate) {
    return nextDunningStageAfter(issued) === candidate;
}
//# sourceMappingURL=ladder.js.map